import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { objToUrl } from '@op/utils/url';
import { useHistory } from 'umi';
import {
  regionEn,
  countryCn,
  countryEn,
  regionCn,
  langCn,
  studioLangEn,
  OPINION_FILTER_CONFIG,
} from '../_config/config';
import { downloadImage } from '@gp/utils/file';
import html2canvas from 'html2canvas';
import { useMemo } from 'react';
import { JumpType, navigate } from '@home/utils/utils';

export const channelColorMap: any = {
  facebook: '#0068FF',
  twitter: '#1DA0F1',
  instagram: '#FC01C2',
  reddit: '#FF7510',
  tiktok: '#507794',
  youtube: '#FF0000',
  youtube_keyword: '#FF0000',
  twitch_keyword: '#9146FF',
  twitch: '#9146FF',
  discord: '#5865F2',
  vk: '#5E82AA',
  cafe: '#19CE60',
  navercafe: '#19CE60',
  navergame: '#65DB71',
  bilibili: '#40C8F1',
  steam: '#1382B3',
  steam_community: '#1382B3',
  'funcom forum': '#E2DFDF',
  kwai: '#FFAE4D',
  xbox: '#0F780F',
  huya: '#FFA800',
  tieba: '#3400FA',
  Others: '#6E7794',
  ytg: '#FF0000',
};

export function canRequest(filter: any = {}) {
  return filter.edition_unified_id && filter.start_time;
}

const urlMap: any = {
  PR: '/opinion/News',
  Influencers: '/opinion/Influencer/BrandOverview',
  'Official Accounts': '/opinion/Accounts/ByChannel',
  Language: '/opinion/Stats/Language',
  Channel: '/opinion/Stats/Channel',
  'Hot Event': {
    total: '/opinion/Summary/HotEvent',
    social: '/opinion/Summary/HotEvent',
    comments: '/opinion/Summary/HotEvent',
  },
  Keyword: {
    total: '/opinion/Overview',
    social: {
      url: '/opinion/Overview',
      params: {
        channel_comments: ['-99999'],
        channel_news: ['-99999'],
        channel_securitys: ['-99999'],
        channel_social: [],
      },
    },
    comments: {
      url: '/opinion/Overview',
      params: {
        channel_comments: ['steam'],
        channel_news: ['-99999'],
        channel_securitys: ['-99999'],
        channel_social: ['-99999'],
      },
    },
  },
  'Community Performance': {
    all: '/opinion/Feeds/Feeds',
    posts: {
      url: '/opinion/Feeds/Feeds',
      params: {
        review_types: ['posts_text', 'sub_text'],
      },
    },
    video: '/opinion/Community/Video',
    streaming: '/opinion/Community/Streaming',
  },
  'Opinion Highlights': '/opinion/Overview/KeyOpinions',
  'Game Store': '/opinion/SteamPerformance/SteamRatings',
  'Trending Publications': '/opinion/Overview/TrendingPublications',
  'Game Store Mobile': '/opinion/GameStore',
};

export function useToOpinionPage({
  filter,
  key,
  subKey,
  simple = false,
  // 默认跳转方式为新开标签页
  jumpType = JumpType.Window,
}: {
  filter: any;
  key: string;
  subKey?: string;
  simple?: boolean;
  jumpType?: JumpType;
}) {
  const history = useHistory();

  function getUrlAndParams() {
    const urlObj = urlMap?.[key];
    if (!urlObj) return { url: '' };
    if (typeof urlObj === 'string') return { url: urlObj, params: {} };

    const realUrlObj = urlObj[subKey || ''];
    if (!realUrlObj) return { url: '' };
    if (typeof realUrlObj === 'string') return { url: realUrlObj, params: {} };
    return realUrlObj;
  }

  function goToOtherPage(otherObj: any = {}) {
    const { edition_unified_id } = filter;
    const objToGo = objToUrl({ gameid: edition_unified_id, ...otherObj });
    const { url, params } = getUrlAndParams();
    if (!url) return;
    if (simple) {
      const resolvedUrl = `${url}?${objToGo}`;
      // resolvedUrl 已经携带筛选器
      navigate(resolvedUrl, { jumpType });
    } else {
      goStatsDetailAction({
        params: { ...filter, ...params, selectAllAccount: true },
        history,
        path: `${url}?${objToGo}`,
        jumpType,
      });
    }
  }

  return { goToOtherPage };
}


/**
 * 缩写转为字符串，如语言en转为English
 * 不确定后续会不会有其他的维度处理，所以提前加上
 * @param abbreviation  缩写
 * @param type          类型  region_en: 地区，country_en: 国家英文, lang_en:语言，country_cn: 国家中文
 * @returns
 */
export function transStudioAbbreviation(
  abbreviation: string,
  type:
  | 'region_en'
  | 'region_cn'
  | 'country_en'
  | 'country_cn'
  | 'lang_en'
  | 'lang_cn',
) {
  // 加toLowerCase的是大小写脱敏处理
  const key = abbreviation?.toLowerCase?.();
  let backText = abbreviation;
  if (type === 'region_en') {
    backText = regionEn[abbreviation];
    backText = backText === 'Global' ? 'Unidentified' : backText;
    if (!backText) {
      backText = countryEn[key];
    }
  } else if (type === 'region_cn') {
    backText = regionCn[abbreviation];
    backText = backText === 'Global' ? 'Unidentified' : backText;
    if (!backText) {
      backText = countryCn[key];
    }
  } else if (type === 'country_en') {
    backText = countryEn[key];
  } else if (type === 'lang_en') {
    backText = studioLangEn[key];
  } else if (type === 'lang_cn') {
    backText = langCn[key];
  } else if (type === 'country_cn') {
    backText = countryCn[key];
  }
  return backText || abbreviation;
}

/**
 * 获取图表浮窗的定位方法，该方法让图表浮窗固定鼠标右上方
 * @param pos    鼠标数值
 * @param size   容器大小
 * @param dom    浮窗dom
 * @param max    最大不超过上方距离
 * @param minY   鼠标Y轴坐标最小距离
 * @returns
 */
export function getChartPositionTop(
  pos: any,
  size: any,
  dom?: any,
  max?: number,
  minY = 0
) {
  const [mouseX, mouseY] = pos;
  const [viewX, viewY] = size?.viewSize || [];
  const isLeft = mouseX < (viewX / 2);
  let bottom = viewY - mouseY + 20;
  // 如果浮窗高度超过max，浮窗下沉对应的差距
  if (max && dom && dom.clientHeight > max) {
    const gap = dom.clientHeight - max + minY;
    if (mouseY <= gap) {
      bottom = bottom - (gap - mouseY);
    }
  }
  // 区分左右2侧
  if (isLeft) {
    return { bottom, left: mouseX };
  }
  return {
    bottom,
    right: viewX - mouseX,
  };
}

export const isNotEmpty = (value: any) => {
  // 检查 null 或 undefined
  if (!value) {
    return false;
  }

  // 检查空字符串
  if (typeof value === 'string' && value.trim() === '') {
    return false;
  }

  // 检查空数组
  if (Array.isArray(value) && value.length === 0) {
    return false;
  }

  // 检查空对象
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return false;
  }

  return true;
};

export const downPreviewImg = async (cfg: {
  node: HTMLElement;
  nodeOptions?: any;
  options?: any;
  fileName?: string;
}) => {
  const { node, fileName } = cfg || {};
  const fileNameTmp = fileName;
  const width = parseFloat(getComputedStyle(node).getPropertyValue('width'));
  const height = parseFloat(getComputedStyle(node).getPropertyValue('height'));
  console.log("★ ~ height>>", height);
  console.log("★ ~ width>>", width);

  const canvas = await html2canvas(node || document.body, {
    scale: 3,
    imageTimeout: 60 * 1000,
    width,
    height,
    // +50补齐弹窗边距和滚动条
    windowWidth: width + 50,
    windowHeight: height,
    scrollX: -2000,
    onclone(document, element) {
      console.log("★ ~ document, element>>", document, element);
    },
  });
  console.log("★ ~ canvas>>", canvas);

  const imageFileSuffix = 'png';
  const dataURL = canvas.toDataURL(imageFileSuffix, 1);
  downloadImage({
    url: dataURL,
    fileName: `${fileNameTmp}.${imageFileSuffix}`,
  });
};

// Studio舆情组件筛选器自定义配置额外需要的参数
export default function useOpinionsFilterConfig(baseFilter: any, componentName: string) {
  return useMemo(() => {
    const defaultFilter = { ...baseFilter };

    const studioConfig = OPINION_FILTER_CONFIG.find(
      (item) => item?.studioid === baseFilter?.studioid
    );

    const componentRule = studioConfig?.componentConfig?.find(
      (c) => c?.name === componentName
    );

    if (componentRule?.filter) {
      Object.assign(defaultFilter, componentRule?.filter);
    }

    return defaultFilter;
  }, [baseFilter, componentName]);
}
